import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { failedResponses } from '../errors/responses';

const c = initContract();

export const ServiceRequestDto = z.object({
  id: z.string(),
  companyId: z.string(),
  requestedBy: z.string(),
  requestType: z.string(),
  createdAt: z.string(),
  customerReference: z.string().optional(),
  payload: z.any(),
  haloTicketId: z.string().nullable().optional(),
  journeyTrackerUrl: z.string().nullable().optional()
});

export type ServiceRequestDto = z.infer<typeof ServiceRequestDto>;

export const ServiceRequestItemSchema = z.object({
  category: z.string(),
  sku: z.string().optional(),
  serialNumber: z.string().nullable().optional(),
  assetTag: z.string().nullable().optional(),
  quantity: z.number(),
  warranty: z.object({
    expiryDate: z.string().nullable(),
    hasAdrWarranty: z.boolean(),
    lifecyclePack: z.string()
  }).optional()
});

export const LogisticsAddressSchema = z.object({
  name: z.string(),
  line1: z.string(),
  line2: z.string().optional().nullable(),
  city: z.string(),
  state: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  postcode: z.string(),
  saveToAddressBook: z.boolean().optional()
});

export const ServiceRequestPayloadSchema = z.object({
  requestContext: z.object({
    requestType: z.string(),
    requestedBy: z.string(),
    mspId: z.string(),
    mspName: z.string(),
    customerId: z.string(),
    customerName: z.string(),
    raisedDateTime: z.string(),
    customerReference: z.string().optional(),
    submissionSource: z.string(),
    haloClientId: z.string().nullable().optional(),
    haloGeneralUserId: z.string().nullable().optional()
  }),
  financialContext: z.object({
    totalEstimatedCost: z.number().int(),
    totalCost: z.number().int().optional(),
    inferredServices: z.array(z.object({
      rateCardId: z.string().optional(),
      name: z.string(),
      quantity: z.number(),
      price: z.number().int()
    })).optional()
  }),
  userContext: z.object({
    userName: z.string(),
    userEmail: z.string(),
    personalEmail: z.string().optional(),
    userMobile: z.string().optional(),
    noCorporateEmail: z.boolean().optional()
  }),
  deployment: z.object({
    isRequired: z.boolean(),
    logistics: z.object({
      schedule: z.object({
        requestedDate: z.string().nullable(),
        isAsapExpedite: z.boolean().nullable()
      }),
      address: LogisticsAddressSchema.optional(),
      siteContact: z.object({
        contactName: z.string().optional(),
        contactEmail: z.string().optional(),
        contactMobile: z.string().optional()
      }).optional(),
      notes: z.string().optional(),
    }).nullable().optional(),
    items: z.array(ServiceRequestItemSchema).nullable().optional()
  }).nullable().optional(),
  collection: z.object({
    isRequired: z.boolean(),
    logistics: z.object({
      schedule: z.object({
        requestedDate: z.string().nullable(),
        isAsapExpedite: z.boolean().nullable()
      }),
      address: LogisticsAddressSchema.optional(),
      siteContact: z.object({
        contactName: z.string().optional(),
        contactEmail: z.string().optional(),
        contactMobile: z.string().optional()
      }).optional(),
      notes: z.string().optional(),
    }).nullable().optional(),
    items: z.array(ServiceRequestItemSchema).nullable().optional()
  }).nullable().optional()
});

export const CreateServiceRequestRequest = z.object({
  requestedBy: z.string(),
  requestType: z.enum(['OffboardUser', 'DeployInventory', 'AdvDeviceReplacement', 'TechRefresh']),
  customerReference: z.string().optional(),
  payload: ServiceRequestPayloadSchema
});

export type CreateServiceRequestRequest = z.infer<typeof CreateServiceRequestRequest>;

export const UpdateServiceRequestRequest = z.object({
  haloTicketId: z.string().nullable().optional()
});

export type UpdateServiceRequestRequest = z.infer<typeof UpdateServiceRequestRequest>;

export default c.router({
  getServiceRequestTypes: {
    method: 'GET',
    path: '/servicerequesttypes',
    responses: {
      200: z.array(z.string()),
      ...failedResponses
    },
    summary: 'Get dynamic list of service request types'
  },
  getServiceRequests: {
    method: 'GET',
    path: '/msp/:mspId/customers/:customerId/service-requests',
    pathParams: z.object({
      mspId: z.string(),
      customerId: z.string()
    }),
    responses: {
      200: z.array(ServiceRequestDto),
      ...failedResponses
    },
    summary: 'Get service requests for a company'
  },
  createServiceRequest: {
    method: 'POST',
    path: '/msp/:mspId/customers/:customerId/service-requests',
    pathParams: z.object({
      mspId: z.string(),
      customerId: z.string()
    }),
    body: CreateServiceRequestRequest,
    responses: {
      201: ServiceRequestDto,
      200: ServiceRequestDto,
      ...failedResponses
    },
    summary: 'Create a new service request'
  },
  updateServiceRequest: {
    method: 'PATCH',
    path: '/msp/:mspId/customers/:customerId/service-requests/:serviceRequestId',
    pathParams: z.object({
      mspId: z.string(),
      customerId: z.string(),
      serviceRequestId: z.string()
    }),
    body: UpdateServiceRequestRequest,
    responses: {
      200: ServiceRequestDto,
      ...failedResponses
    },
    summary: 'Update a service request'
  }
});
